import type { ContactEmail, ContactPhoneNumber } from '@/types/contact';

// ─── Lead Stage ───────────────────────────────────────────────────────────────

export interface LeadStage {
  id: string;
  name: string;
  color: string;
  sortOrder: number;
  isDefault: boolean;
  isWon: boolean;
  isLost: boolean;
  /**
   * Which terminal a lost stage is. Both "Lost" and "Disqualified" carry
   * `isLost`, so this is the only field that separates them.
   *
   * Optional because a tenant's stages may be served by an operations build
   * that predates the column; `resolveDisqualifiedStage` falls back to the name
   * in that case, which is what the whole board did before this existed.
   */
  isDisqualified?: boolean;
  isActive: boolean;
  slaOverrideMinutes?: number | null;
}

export interface CreateLeadStageDto {
  name: string;
  color?: string;
  sortOrder?: number;
  isDefault?: boolean;
  isWon?: boolean;
  isLost?: boolean;
  /**
   * Sent alongside `isLost`, never on its own — the API refuses a disqualified
   * stage that is not also lost. Omitting it entirely is what made the terminal
   * split unmanageable: a tenant could build a junk column but not mark it one.
   */
  isDisqualified?: boolean;
  slaOverrideMinutes?: number | null;
}

export interface UpdateLeadPipelineStageDto {
  id: string;
  name?: string;
  color?: string;
  sortOrder?: number;
  isDefault?: boolean;
  isWon?: boolean;
  isLost?: boolean;
  /**
   * Always sent when the terminal changes, including as `false`. The API merges
   * this patch onto the stored row, so leaving it out while clearing `isLost`
   * asks for a disqualified-but-open stage and is rejected — which is how a
   * tenant gets locked out of repurposing their Disqualified column.
   */
  isDisqualified?: boolean;
  isActive?: boolean;
  slaOverrideMinutes?: number | null;
}

export interface ReorderLeadStagesDto {
  stages: { id: string; sortOrder: number }[];
}

// ─── Lead Source (matches backend enum) ───────────────────────────────────────

export type LeadSource =
  | 'ANGI'
  | 'THUMBTACK'
  | 'YELP'
  | 'GOOGLE_LSA'
  | 'GOOGLE_ADS'
  | 'FACEBOOK'
  | 'INSTAGRAM'
  | 'NEXTDOOR'
  | 'HOMEADVISOR'
  | 'WEBSITE'
  | 'ONLINE_BOOKING'
  | 'PHONE_CALL'
  | 'REFERRAL'
  | 'WALK_IN'
  | 'EMAIL'
  | 'CHAT'
  | 'INBOX'
  | 'MANUAL'
  | 'API'
  | 'OTHER'
  // Suite F (F-09): system-assigned bucket for an inbound that declared no
  // channel. Filterable, but deliberately absent from LEAD_SOURCES so nobody
  // can *pick* it — an acquisition is never guessed.
  | 'UNKNOWN'
  // Persisted by the backend but never pickable, and never a real acquisition
  // channel: ZAPIER is a transport (NOT-592 §1.2) and the DIRECT_* pair comes
  // from the auto-create path §1.8 retires. They belong in the union so the
  // rows carrying them stay filterable until U27 removes the values.
  | 'ZAPIER'
  | 'DIRECT_ESTIMATE'
  | 'DIRECT_JOB';

// ─── Lead Outcome (matches backend enum) ─────────────────────────────────────

/**
 * Suite F (F-03): a lead that converts is CONVERTED, never "Won" — leads have
 * no Won, that concept lives on estimates. Typed (rather than a bare `string`)
 * so a stale literal like 'WON' fails the typecheck instead of silently
 * evaluating to false at runtime.
 */
// U-TERM1 added the third terminal: junk (spam, duplicates) is disqualified
// rather than lost, so loss reporting describes only real losses. Keep this in
// step with operations' LeadOutcome — a value the API can send but this union
// omits is invisible to every exhaustive check in the SPA.
export type LeadOutcome = 'CONVERTED' | 'LOST' | 'DISQUALIFIED';

/** Why a real opportunity did not close. Mirrors operations' `LeadLossReason`. */
export type LeadLossReason =
  | 'PRICE'
  | 'TIMING'
  | 'WENT_WITH_COMPETITOR'
  | 'NO_RESPONSE'
  | 'OUT_OF_SCOPE'
  | 'OUT_OF_AREA'
  | 'WRONG_SERVICE'
  | 'CANCELLED'
  | 'OTHER';

/** Mirrors operations' `LeadDisqualificationReason` — all eight the API accepts. */
export type LeadDisqualificationReason =
  | 'OUT_OF_AREA'
  | 'NO_BUDGET'
  | 'NOT_READY'
  | 'WRONG_SERVICE'
  | 'SPAM'
  | 'DUPLICATE'
  | 'COMPETITOR'
  | 'OTHER';

/**
 * The subset meaning the lead was never an opportunity, and the only reasons
 * `close({ outcome: 'DISQUALIFIED' })` accepts — the endpoint rejects the other
 * six outright, since they describe a real lead that did not close and belong on
 * a LOST close with a loss reason. Mirrors `isJunkDisqualification`.
 */
export type JunkDisqualificationReason = Extract<LeadDisqualificationReason, 'SPAM' | 'DUPLICATE'>;

// ─── Lead Priority / Urgency (matches backend enums) ──────────────────────────

/** Mirrors operations' `LeadPriority`. */
export const LEAD_PRIORITY_VALUES = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'] as const;
export type LeadPriorityValue = (typeof LEAD_PRIORITY_VALUES)[number];

/** Mirrors operations' `LeadUrgency` — a distinct scale from priority. */
export const LEAD_URGENCY_VALUES = ['STANDARD', 'SOON', 'URGENT', 'EMERGENCY'] as const;
export type LeadUrgencyValue = (typeof LEAD_URGENCY_VALUES)[number];

// ─── Assignment ────────────────────────────────────────────────────────────

export type AssignmentMethod = 'AUTO' | 'MANUAL' | 'ROUND_ROBIN' | 'AI';

/**
 * One span in a lead's assignment history. `assignedAt`/`unassignedAt` bound
 * the ownership window; a null `unassignedAt` marks the still-current owner.
 */
export interface LeadAssignmentSpan {
  userId: string;
  // NOT-486: backend-resolved display name for `userId`. Resolved straight from
  // auth_users (including members who have left the company), so the timeline
  // isn't limited to the frontend's active-only roster. Null when the id has no
  // matching user; the client then falls back to its own roster lookup.
  userName?: string | null;
  assignedAt: string | null;
  unassignedAt: string | null;
  assignedBy: string | null;
  reason: string | null;
}

export interface LeadEnrichmentFlags {
  serviceCategoryAutoMapped?: boolean;
  serviceCategoryId?: string;
  serviceCategoryName?: string;
  serviceCategoryReason?: string;
  isMember?: boolean;
  isVip?: boolean;
  vipReason?: string;
  membershipStatus?: string;
  membershipPlanName?: string;
  blockedContact?: boolean;
  blockedReason?: string | null;
  autoDisqualified?: boolean;
  autoDisqualificationReason?: string;
  multiSource?: boolean;
  multiSourceReason?: string;
  relatedLeadIds?: string[];
  relatedLeadSources?: string[];
  relatedLeads?: Array<{
    id: string;
    source: string;
    sourceId?: string | null;
    leadNumber?: string | null;
    title?: string | null;
    createdAt?: string | null;
  }>;
}

// ─── Lead Types ──────────────────────────────────────────────────────────────

export interface LeadContact {
  id: string;
  firstName: string;
  lastName?: string;
  displayName?: string;
  primaryEmail?: string | null;
  emails?: ContactEmail[];
  primaryPhone?: string | null;
  phoneNumbers?: ContactPhoneNumber[];
  companyName?: string | null;
}

export interface Lead {
  id: string;
  leadNumber: string;
  title?: string | null;
  description?: string | null;
  serviceRequested?: string | null;
  contactId: string;
  contact?: LeadContact;
  stageId: string;
  stage: LeadStage;
  source: LeadSource;
  sourceId?: string | null;
  assignedTo?: string | null;
  // NOT-486: backend-resolved display name for `assignedTo`, resolved from
  // auth_users (including departed members), so the header shows the owner's
  // name even when they're absent from the frontend's active-only roster. Null
  // when unresolved; the client then falls back to its roster lookup.
  assignedToName?: string | null;
  assignedBy?: AssignmentMethod | null;
  assignmentHistory?: LeadAssignmentSpan[];
  teamId?: string | null;
  priority: string;
  urgency: string;
  qualificationStatus: string;
  estimatedValue?: number | null;
  followUpDate?: string | null;
  outcome?: LeadOutcome | null;
  lossReason?: string | null;
  lossReasonDetail?: string | null;
  competitorName?: string | null;
  disqualificationReason?: string | null;
  disqualificationNotes?: string | null;
  convertedEstimateId?: string | null;
  convertedJobId?: string | null;
  actualValue?: number | null;
  closedAt?: string | null;
  firstResponseAt?: string | null;
  lastActivityAt?: string | null;
  leadScore: number;
  pWin?: number | null;
  scoreEngine?: 'SCORECARD' | 'POOLED_MODEL' | 'TENANT_MODEL';
  scoreScaleVersion?: string;
  freshness?: 'FRESH' | 'AGING' | 'STALE';
  enrichmentFlags?: LeadEnrichmentFlags | null;
  serviceAddressId?: string | null;
  // NOT-489/445: set when the lead was auto-created from a direct estimate/job
  // or an online/phone booking. 'DIRECT_JOB' means booked-at-creation
  // ("self-booked") — no lead work applies, so priority/freshness are N/A.
  autoCreatedReason?: string | null;
  // Computed fields from backend
  leadGrade?: string | null;
  isClosed?: boolean;
  slaBreached?: boolean;
  timeToFirstResponse?: number | null;
  timeToClose?: number | null;
  // NOT-478: resolved service address for display (backend-computed).
  serviceAddress?: LeadResolvedAddress | null;
  customFields?: Record<string, unknown>;
  isArchived?: boolean;
  createdAt: string;
  updatedAt: string;
}

// ─���─ Notes & Activity ─────────────────────────────────────────────────────────

export interface LeadNote {
  id: string;
  note: string;
  createdAt: string;
  updatedAt: string;
}

export interface LeadActivity {
  id: string;
  type: string;
  content?: string | null;
  userId?: string | null;
  timestamp: string;
  durationSeconds?: number | null;
  callOutcome?: string | null;
  isFirstResponse: boolean;
  createdAt: string;
}

// ─── List params & response ──────────────────────────────────────────────────

/**
 * The `assignedTo` value that asks for leads with no owner.
 *
 * A reserved word rather than an id, because "nobody owns this" has no id to
 * name. The server exempts exactly this string from its UUID validation
 * (`UNASSIGNED_LEAD_FILTER` in operations' `lead.interfaces.ts`) and turns it
 * into `assignedTo IS NULL`; the two spellings must stay identical, and any
 * other word is a 400 rather than an empty page.
 */
export const UNASSIGNED_LEAD_FILTER = 'unassigned';

export interface LeadListParams {
  page?: number;
  limit?: number;
  search?: string;
  stage?: string;
  source?: LeadSource;
  assignedTo?: string;
  priority?: string;
  leadGrade?: 'A' | 'B' | 'C' | 'D' | 'F';
  slaStatus?: 'BREACHED' | 'MET' | 'PENDING';
  qualificationStatus?: string;
  contactId?: string;
  createdAfter?: string;
  createdBefore?: string;
  followUpAfter?: string;
  followUpBefore?: string;
  lastActivityAfter?: string;
  lastActivityBefore?: string;
  // The amount a lead DISPLAYS (`resolveLeadValueFact`), not raw
  // `estimatedValue`: a won lead matches on its revenue. The server also still
  // accepts the deprecated `estimatedValueMin/Max` spelling and folds it onto
  // these, but nothing in this app should send it — a request keyed on the raw
  // column filters rows by a number they do not show.
  valueMin?: number;
  valueMax?: number;
  sortBy?:
    | 'leadNumber'
    | 'createdAt'
    | 'leadScore'
    | 'value'
    | 'followUpDate'
    | 'lastActivityAt'
    | 'slaDeadline'
    | 'priority';
  sortDir?: 'asc' | 'desc';
  [key: string]: unknown;
}

export interface LeadListResponse {
  data: Lead[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasMore: boolean;
}

// NOT-514 #2: one entry per pipeline stage with the TRUE lead count and summed
// estimated value for the current filter set — the board reads these for its
// per-column header instead of counting the (paginated) cards it has loaded.
export interface LeadPipelineSummaryEntry {
  stageId: string;
  stageName: string;
  color?: string | null;
  sortOrder: number;
  isWon: boolean;
  isLost: boolean;
  count: number;
  totalEstimatedValue: number;
}

export interface LeadSavedViewFilters {
  q?: string;
  stage?: string;
  source?: LeadSource;
  /** User id, or `UNASSIGNED_LEAD_FILTER`. Absent means every assignee. */
  assignedTo?: string;
  priority?: string;
  leadGrade?: 'A' | 'B' | 'C' | 'D' | 'F';
  qualificationStatus?: string;
  slaStatus?: 'BREACHED' | 'MET' | 'PENDING';
  estimatedValueOp?: 'eq' | 'gt' | 'lt' | 'gte' | 'lte';
  estimatedValueValue?: string;
  sort?: number;
  dateFrom?: string;
  dateTo?: string;
}

export interface LeadSavedView {
  id: string;
  name: string;
  filters: LeadSavedViewFilters;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateLeadSavedViewDto {
  name: string;
  filters: LeadSavedViewFilters;
  isDefault?: boolean;
}

export interface UpdateLeadSavedViewDto {
  id: string;
  name?: string;
  filters?: LeadSavedViewFilters;
  isDefault?: boolean;
}

// ─── DTOs ─────────────────────────────────────────────────────────────────────

/** A freshly-typed service address captured on the lead form (NOT-478). */
export interface LeadServiceAddressInput {
  street1: string;
  street2?: string;
  city: string;
  state: string;
  postalCode: string;
  country?: string;
}

/** Backend-resolved service address for display (note: street/zip, not street1/postalCode). */
export interface LeadResolvedAddress {
  street: string;
  street2?: string;
  city: string;
  state: string;
  zip: string;
  country?: string;
}

export interface CreateLeadDto {
  contactId?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  title?: string;
  description?: string;
  serviceRequested?: string;
  source: LeadSource;
  stageId?: string;
  priority?: string;
  urgency?: string;
  estimatedValue?: number;
  assignedTo?: string;
  followUpDate?: string | null;
  serviceAddressId?: string | null;
  serviceAddress?: LeadServiceAddressInput;
  customFields?: Record<string, unknown>;
}

export interface UpdateLeadDto {
  id: string;
  title?: string;
  description?: string;
  stageId?: string;
  priority?: string;
  urgency?: string;
  qualificationStatus?: string;
  // All eight, unlike the close endpoint's junk-only field: `update` accepts any
  // of them and derives the terminal from it — the six that describe a real loss
  // are stored as LOST with a mapped loss reason.
  disqualificationReason?: LeadDisqualificationReason;
  disqualificationNotes?: string;
  estimatedValue?: number;
  serviceRequested?: string;
  serviceAddressId?: string | null;
  serviceAddress?: LeadServiceAddressInput;
  followUpDate?: string;
  teamId?: string;
  customFields?: Record<string, unknown>;
}

export interface UpdateLeadStageDto {
  id: string;
  stageId: string;
}

/**
 * `POST /leads/:id/close`, shaped as a union because the endpoint's requirements
 * differ per outcome and a flat interface cannot say so — it let a caller build
 * `{ outcome: 'DISQUALIFIED' }` with no reason attached, which the server
 * answers with a 400. The field it needed was not merely optional here, it was
 * absent, so the valid request could not be expressed at all without `as any`.
 *
 * Each member mirrors what `LeadsService.close` validates:
 * - CONVERTED takes neither reason.
 * - LOST requires a `lossReason`, and `competitorName` alongside
 *   WENT_WITH_COMPETITOR (a DTO constraint, not a convention).
 * - DISQUALIFIED requires a junk reason; the endpoint rejects the other six
 *   rather than storing a real loss as junk with no loss reason.
 *
 * `lossReasonDetail` is free text a human reads and rides both terminals.
 */
export type CloseLeadDto =
  | { outcome: 'CONVERTED' }
  | {
      outcome: 'LOST';
      lossReason: LeadLossReason;
      lossReasonDetail?: string;
      competitorName?: string;
    }
  | {
      outcome: 'DISQUALIFIED';
      disqualificationReason: JunkDisqualificationReason;
      lossReasonDetail?: string;
    };

export interface MergeLeadDto {
  duplicateLeadId: string;
  allowCrossContact?: boolean;
  reason?: string;
}

export interface AssignLeadDto {
  userId: string;
  method?: 'AUTO' | 'MANUAL' | 'ROUND_ROBIN' | 'AI';
}

export interface BulkAssignLeadsDto extends AssignLeadDto {
  ids: string[];
}

// Result of running the assignment engine on a single lead.
export interface AutoAssignLeadResult {
  assigned: boolean;
  lead: Lead;
}

export interface BulkAutoAssignLeadsDto {
  ids: string[];
}

export interface BulkAutoAssignLeadsResult {
  requested: number;
  assigned: number;
  results: Array<{ leadId: string; assigned: boolean; lead: Lead }>;
}

export interface ConvertLeadToJobResponse {
  jobId: string;
  bookingId?: string;
}

// ─── Pipeline summary ─────────────────────────────────────────────────────────

export interface PipelineStageSummary {
  stageId: string;
  stageName: string;
  color: string;
  sortOrder: number;
  isWon: boolean;
  isLost: boolean;
  count: number;
  totalEstimatedValue: number;
}
